// Story 8.10 (ADO-2019), Q2: "N HHT orders waiting" before a manual send (the 8.10 Figma
// exports, "Q2 · Send guard, HHT orders waiting"). The design system's Modal as every other
// confirm: the centred dialog on desktop and tablet, with the secondary "Send anyway" then the
// primary "Review HHT orders" at the right; the compact bottom sheet on mobile (as "Order
// sent"), "Review HHT orders" first, both full width. "Send anyway" sends as normal; "Review
// HHT orders" opens /hht-orders without sending; Close, Escape and the scrim cancel. The
// question is `useHhtSendGuardStore`'s; each page with a manual send mounts this once, and its
// unmount drops a question still open.
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Button, Modal, useSheetLayout } from '../../design-system';
import { useHhtSendGuardStore } from '../../hht/useHhtSendGuardStore';
import { HHT_ORDERS_ROUTE } from './hhtRoutes';
import styles from './HhtOrders.module.css';

/** The dialog's title and text for `count` waiting orders (Figma copy, made singular for one). */
export function hhtSendGuardCopy(count: number): { title: string; text: string } {
  return count === 1
    ? {
        title: '1 HHT order waiting',
        text: "It's not included in this order. Review it first, or send this order now and add it to your next one.",
      }
    : {
        title: `${count} HHT orders waiting`,
        text: "They're not included in this order. Review them first, or send this order now and add them to your next one.",
      };
}

export function HhtSendGuardDialog() {
  const navigate = useNavigate();
  const request = useHhtSendGuardStore((s) => s.request);
  const sendAnyway = useHhtSendGuardStore((s) => s.sendAnyway);
  const dismiss = useHhtSendGuardStore((s) => s.dismiss);
  const sheet = useSheetLayout();
  // The count the question opened with, kept while it closes, so the title never reads
  // "0 HHT orders waiting" on the way out (should the Modal ever animate its exit).
  const [shownCount, setShownCount] = useState(0);
  if (request !== null && request.waitingCount !== shownCount) setShownCount(request.waitingCount);
  const copy = hhtSendGuardCopy(request?.waitingCount ?? shownCount);
  // A question belongs to the page that asked it: leaving that page drops it, so another
  // page's dialog never opens on it or runs its send.
  useEffect(() => () => useHhtSendGuardStore.getState().reset(), []);

  const review = () => {
    useHhtSendGuardStore.getState().reset();
    navigate(HHT_ORDERS_ROUTE);
  };
  const sendAnywayButton = (
    <Button key="send" variant="secondary" fullWidth={sheet} onClick={sendAnyway}>
      Send anyway
    </Button>
  );
  const reviewButton = (
    <Button key="review" variant="primary" fullWidth={sheet} onClick={review}>
      Review HHT orders
    </Button>
  );

  return (
    <Modal
      open={request !== null}
      onClose={dismiss}
      title={copy.title}
      placement={sheet ? 'bottom' : 'center'}
      sheetGutter="compact"
      footer={sheet ? [reviewButton, sendAnywayButton] : [sendAnywayButton, reviewButton]}
    >
      <p className={styles.sendGuardText}>{copy.text}</p>
    </Modal>
  );
}
